import React, { useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { format, parseISO } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCheckCircle, FiCalendar, FiUsers, FiMail, FiPhone, FiMessageSquare, FiArrowLeft } = FiIcons;

const BookingConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const bookingData = location.state?.bookingData;

  useEffect(() => {
    if (!bookingData) {
      navigate('/rates');
    }
    window.scrollTo(0, 0);
  }, [bookingData, navigate]);

  if (!bookingData) return null;

  return (
    <div className="w-full min-h-screen bg-brand-cream pt-[100px]">
      <section className="py-16 bg-brand-olive text-white text-center">
        <div className="container mx-auto px-6">
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <SafeIcon icon={FiCheckCircle} className="text-4xl text-brand-cream" />
          </motion.div>
          <h1 className="font-serif text-3xl md:text-5xl uppercase tracking-[0.2em] mb-4">Booking Request Received</h1>
          <p className="font-sans text-brand-cream/80 uppercase tracking-widest text-xs font-bold">Ref: RV-{Math.floor(Math.random() * 10000).toString().padStart(4, '0')}</p>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-8">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white p-8 md:p-12 shadow-2xl border border-brand-olive/5">
                <h2 className="font-serif text-2xl text-brand-olive uppercase tracking-widest mb-8 border-b border-brand-olive/10 pb-4">Stay Summary</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-brand-cream flex items-center justify-center shrink-0"><SafeIcon icon={FiCalendar} className="text-brand-olive" /></div>
                      <div>
                        <p className="text-[10px] font-black uppercase tracking-widest text-brand-olive/40 mb-1">Check-In</p>
                        <p className="font-sans font-bold text-lg text-brand-olive">{format(parseISO(bookingData.check_in), 'MMMM dd, yyyy')}</p>
                        <p className="text-xs text-brand-olive/60 mt-1">From 2:00 PM</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-brand-cream flex items-center justify-center shrink-0"><SafeIcon icon={FiCalendar} className="text-brand-olive" /></div>
                      <div>
                        <p className="text-[10px] font-black uppercase tracking-widest text-brand-olive/40 mb-1">Check-Out</p>
                        <p className="font-sans font-bold text-lg text-brand-olive">{format(parseISO(bookingData.check_out), 'MMMM dd, yyyy')}</p>
                        <p className="text-xs text-brand-olive/60 mt-1">Until 11:00 AM</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-brand-cream flex items-center justify-center shrink-0"><SafeIcon icon={FiUsers} className="text-brand-olive" /></div>
                      <div>
                        <p className="text-[10px] font-black uppercase tracking-widest text-brand-olive/40 mb-1">Guests</p>
                        <p className="font-sans font-bold text-lg text-brand-olive">{bookingData.guests} Guests</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-brand-cream flex items-center justify-center shrink-0"><SafeIcon icon={FiMessageSquare} className="text-brand-olive" /></div>
                      <div>
                        <p className="text-[10px] font-black uppercase tracking-widest text-brand-olive/40 mb-1">Total Estimated</p>
                        <p className="font-sans font-bold text-lg text-brand-olive">₱{bookingData.total_price.toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                </div>
                {bookingData.message && (
                  <div className="mt-10 pt-8 border-t border-brand-olive/10">
                    <p className="text-[10px] font-black uppercase tracking-widest text-brand-olive/40 mb-3">Your Message to Host</p>
                    <p className="text-brand-olive/80 font-medium italic leading-relaxed bg-brand-cream/30 p-4 border-l-2 border-brand-olive">"{bookingData.message}"</p>
                  </div>
                )}
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white p-8 md:p-12 shadow-2xl border border-brand-olive/5">
                <h3 className="font-serif text-xl text-brand-olive uppercase tracking-widest mb-6">What's Next?</h3>
                <div className="space-y-6">
                  {["Our team will review your request and check the availability.", "You will receive a confirmation call or email within 24 hours.", "We will provide payment instructions (G-Cash or Bank Transfer) to secure your dates.", "Once payment is verified, your booking will be officially confirmed."].map((step, i) => (
                    <div key={i} className="flex gap-4">
                      <span className="w-6 h-6 rounded-full bg-brand-olive text-white flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">{i + 1}</span>
                      <p className="text-brand-olive/70 text-sm font-medium leading-relaxed">{step}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>

            <div className="space-y-8">
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }} className="bg-brand-olive p-8 text-white shadow-xl">
                <h3 className="font-serif text-lg uppercase tracking-widest mb-6 border-b border-white/10 pb-4">Contact Info</h3>
                <div className="space-y-4">
                  <p className="text-xs font-bold uppercase tracking-widest text-brand-cream/60">Guest Name</p>
                  <p className="font-sans font-bold text-base mb-6">{bookingData.guest_name}</p>
                  <div className="flex items-center gap-3 text-sm text-brand-cream/80"><SafeIcon icon={FiMail} /><a href={`mailto:${bookingData.email}`} className="hover:text-white hover:underline underline-offset-4 transition-all">{bookingData.email}</a></div>
                  <div className="flex items-center gap-3 text-sm text-brand-cream/80"><SafeIcon icon={FiPhone} /><a href={`tel:${bookingData.phone}`} className="hover:text-white hover:underline underline-offset-4 transition-all">{bookingData.phone}</a></div>
                </div>
              </motion.div>
              <Link to="/" className="flex items-center justify-center gap-2 text-brand-olive/60 hover:text-brand-olive transition-colors font-bold uppercase tracking-widest text-[10px]"><SafeIcon icon={FiArrowLeft} /> Back to Home</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BookingConfirmation;